import React, { useEffect, useState } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { styled, Typography } from "@mui/material";
import { HttpService } from "../../service/HttpService";
import { useAuth } from "../../context/AuthProvider";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import dayjs from "dayjs";

const DryRoomArchive = ({ roomId, harvestedDate }) => {
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const fetchData = async () => {
    if (!roomId) return;
    setLoading(true);
    try {
      const res = await httpService.get(
        `dry-room/archive/${roomId}?harvested_date=${harvestedDate}`,
      );
      if (res?.data) {
        setData(res.data);
      }
    } catch (error) {
      console.error("Error fetching archived dry room data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [roomId, harvestedDate]);

  const StyledGridOverlay = styled("div")(() => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  }));

  const getColorStyle = (color) => {
    const colors = {
      RED: "#ffebee",
      YELLOW: "#fff9c4",
      GREEN: "#e8f5e9",
    };
    return colors[color] || "transparent";
  };

  const columns = [
    {
      field: "phase_name",
      headerName: "Growth Phase",
      flex: 1,
      minWidth: 200,
      headerAlign: "left",
      align: "left",
    },
    {
      field: "temp_target",
      headerName: "Temp Target",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <div style={{ width: "100%" }}>{parseFloat(params.value) || ""}</div>
      ),
    },
    {
      field: "high_temp",
      headerName: "High Temp",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <div
          style={{
            backgroundColor: params.value
              ? getColorStyle(params.row.high_temp_color)
              : "unset",
            padding: "8px",
            width: "100%",
          }}
        >
          {parseFloat(params.value) || ""}
        </div>
      ),
    },
    {
      field: "low_temp",
      headerName: "Low Temp",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <div
          style={{
            backgroundColor:
              params.value > 0
                ? getColorStyle(params.row.low_temp_color)
                : "unset",
            padding: "8px",
            width: "100%",
          }}
        >
          {parseFloat(params.value) || ""}
        </div>
      ),
    },
    {
      field: "avg_temp",
      headerName: "Average Temp",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <div
          style={{
            backgroundColor:
              params.value > 0
                ? getColorStyle(params.row.avg_temp_color)
                : "unset",
            padding: "8px",
            width: "100%",
          }}
        >
          {parseFloat(params.value) || ""}
        </div>
      ),
    },
    {
      field: "humidity_target",
      headerName: "Humidity Target",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <div style={{ width: "100%" }}>{parseFloat(params.value) || ""}</div>
      ),
    },
    {
      field: "high_humidity",
      headerName: "High Humidity",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <div
          style={{
            backgroundColor:
              params.value > 0
                ? getColorStyle(params.row.high_humidity_color)
                : "unset",
            padding: "8px",
            width: "100%",
          }}
        >
          {parseFloat(params.value) || ""}
        </div>
      ),
    },
    {
      field: "low_humidity",
      headerName: "Low Humidity",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <div
          style={{
            backgroundColor:
              params.value > 0
                ? getColorStyle(params.row.low_humidity_color)
                : "unset",
            padding: "8px",
            width: "100%",
          }}
        >
          {parseFloat(params.value) || ""}
        </div>
      ),
    },
    {
      field: "avg_humidity",
      headerName: "Average Humidity",
      flex: 1,
      minWidth: 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => (
        <div
          style={{
            backgroundColor:
              params.value > 0
                ? getColorStyle(params.row.avg_humidity_color)
                : "unset",
            padding: "8px",
            width: "100%",
          }}
        >
          {parseFloat(params.value) || ""}
        </div>
      ),
    },
    {
      field: "archived_at",
      headerName: "Archived Date",
      minWidth: 150,
      renderCell: (params) =>
        params.value ? (
          <div style={{ width: "100%" }}>
            {dayjs(params.value).format("DD-MM-YYYY")}
          </div>
        ) : (
          ""
        ),
    },
  ];

  return (
    <>
      {loading && <FullScreenLoader />}
      <div className="company pt-4">
        <div
          className="common_content"
          style={{
            height: `${data?.length > 0 ? "" : "200px"}`,
            width: "100%",
            marginTop: 12,
          }}
        >
          <DataGrid
            rows={data}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            sx={{
              border: "1px solid #E0E0E0",
              [`& .${gridClasses.cell}`]: { py: 1 },
              minHeight: 200,
            }}
            hideFooter
            showCellVerticalBorder
            showColumnVerticalBorder
            autoHeight={data?.length > 0}
            slots={{
              noRowsOverlay: () => (
                <StyledGridOverlay>
                  <Typography variant="body2" color="textSecondary">
                    No Data Found
                  </Typography>
                </StyledGridOverlay>
              ),
            }}
          />
        </div>
      </div>
    </>
  );
};

export default DryRoomArchive;
